import {defineStore} from 'pinia'
import axios, {AxiosError, Method} from 'axios'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {IUploadSession} from '@toolify/client/src/modules/files/upload/types/IUploadSession'
import {IUploadSessionPart} from '@toolify/client/src/modules/files/upload/types/IUploadSessionPart'
import {IUploadSessionState} from '@toolify/client/src/modules/files/upload/types/IUploadSessionState'
import {ICreateUploadSessionBody} from '@toolify/client/src/modules/files/upload/types/ICreateUploadSessionBody'
import {getRuntimeEnv} from '@toolify/client/src/stores/AdminRuntimeStore/functions/getRuntimeEnv'
import {isGodModeEnabled} from '@toolify/client/src/stores/AdminRuntimeStore/functions/isGodModeEnabled'
import {getViewAsToken} from '../../ViewAsStore/functions/getViewAsToken'
import {ViewAsHeaderName} from '../../ViewAsStore/constants/ViewAsHeaderName'

const REQUEST_TIMEOUT_MS = 30 * 1000
// complete assembles the object server-side (paginated ListParts + CompleteMultipartUpload + link),
// which scales with part count and routinely outlives a request-sized timeout on multi-GB uploads
const COMPLETE_REQUEST_TIMEOUT_MS = 15 * 60 * 1000

interface IUploadSessionRequestOptions {
  method: Method
  path: string
  data?: unknown
  signal?: AbortSignal
  timeoutMs?: number
}

interface IKnownErrorPayload {
  name: KnownError
  message: string
  extra: Record<string, unknown>
}

function getKnownErrorPayload(error: unknown): IKnownErrorPayload {
  if(!(error instanceof AxiosError)) {
    return null
  }
  const responseData = error.response?.data as {error?: IKnownErrorPayload}
  return responseData?.error || null
}

function toRequestError(error: unknown): unknown {
  const knownErrorPayload = getKnownErrorPayload(error)
  if(!knownErrorPayload) {
    return error
  }
  return new ApiException(
    knownErrorPayload.name,
    knownErrorPayload.message,
    (error as AxiosError).response.status,
    true,
    knownErrorPayload.extra,
  )
}

// these calls deliberately bypass useApiRequestStore: its queue drops to a single slot after any
// network failure and holds a slot for the whole request, so a call made from inside a running
// upload would never get scheduled
async function sendRequest<TResponse>(options: IUploadSessionRequestOptions): Promise<TResponse> {
  const authStore = useAuthStore()
  await authStore.renewTokenIfNeeded()
  const token = await authStore.getToken()
  const currentLinkId = authStore.currentLinkId
  const response = await axios({
    method: options.method,
    url: `${getRuntimeEnv().ApiHost}${options.path}`,
    ...(options.data === undefined ? {} : {data: options.data}),
    ...(options.signal ? {signal: options.signal} : {}),
    timeout: options.timeoutMs ?? REQUEST_TIMEOUT_MS,
    headers: {
      ...(token
        ? {
          Authorization: `Bearer ${token}`,
        }
        : {}),
      ...(currentLinkId
        ? {
          'X-Current-Link-Id': currentLinkId,
        }
        : {}),
      ...(isGodModeEnabled()
        ? {
          'X-God-Mode': '1',
        }
        : {}),
      ...(getViewAsToken()
        ? {
          [ViewAsHeaderName]: getViewAsToken(),
        }
        : {}),
      'X-Request-Compression': 'false',
    },
  })
  return response.data?.data as TResponse
}

async function makeRequest<TResponse>(options: IUploadSessionRequestOptions): Promise<TResponse> {
  try {
    return await sendRequest<TResponse>(options)
  } catch(error) {
    const knownErrorPayload = getKnownErrorPayload(error)
    const shouldRenewToken = knownErrorPayload?.name === KnownError.TOKEN_EXPIRED
    const shouldCreateSession = knownErrorPayload?.name === KnownError.MISSING_TOKEN
    if(!shouldRenewToken && !shouldCreateSession) {
      throw toRequestError(error)
    }
    const authStore = useAuthStore()
    if(shouldRenewToken) {
      await authStore.renewToken()
    } else {
      await authStore.createSession()
    }
    try {
      return await sendRequest<TResponse>(options)
    } catch(retryError) {
      throw toRequestError(retryError)
    }
  }
}

function mapUploadSession(uploadSession: IToJson<IUploadSession>): IUploadSession {
  return {
    ...uploadSession,
    expiresAt: new Date(uploadSession.expiresAt),
  }
}

export const useUploadSessionApiStore = defineStore('uploadSessionApi', {
  actions: {
    async createUploadSession(
      body: ICreateUploadSessionBody,
      signal?: AbortSignal,
    ): Promise<IUploadSession> {
      const {uploadSession} = await makeRequest<{uploadSession: IToJson<IUploadSession>}>({
        method: 'post',
        path: '/upload-session',
        data: body,
        signal,
      })
      return mapUploadSession(uploadSession)
    },

    async getUploadSession(
      uploadSessionId: string,
      signal?: AbortSignal,
    ): Promise<IUploadSessionState> {
      const {uploadSession} = await makeRequest<{uploadSession: IUploadSessionState}>({
        method: 'get',
        path: `/upload-session/${uploadSessionId}`,
        signal,
      })
      return uploadSession
    },

    async signUploadSessionParts(
      uploadSessionId: string,
      partNumbers: number[],
      signal?: AbortSignal,
    ): Promise<IUploadSessionPart[]> {
      const {parts} = await makeRequest<{parts: IUploadSessionPart[]}>({
        method: 'post',
        path: `/upload-session/${uploadSessionId}/parts`,
        data: {
          partNumbers,
        },
        signal,
      })
      return parts
    },

    // the response shape is decided by the upload target strategy, so it stays raw json for the call site to map
    async completeUploadSession(
      uploadSessionId: string,
      signal?: AbortSignal,
    ): Promise<unknown> {
      return await makeRequest<unknown>({
        method: 'post',
        path: `/upload-session/${uploadSessionId}/complete`,
        data: {},
        signal,
        timeoutMs: COMPLETE_REQUEST_TIMEOUT_MS,
      })
    },

    async abortUploadSession(uploadSessionId: string): Promise<void> {
      await makeRequest<{success: boolean}>({
        method: 'post',
        path: `/upload-session/${uploadSessionId}/abort`,
        data: {},
      })
    },
  },
})
